import CommitteeMemberApi from "@/api/CommitteeMemberApi";
import AdministrationPayer, {
  AdministrationPayer_Request,
} from "@/models/APIModels/AdministrationPayer";
import AdministrationPayerViewModel from "@/models/ViewModels/AdministrationPayerViewModel";
import PaymentSourceModel from "@/models/ViewModels/PaymentSourceModel";
import moment from "moment";
import IncomingPaymentTransactionList from "@/models/APIModels/IncomingPaymentTransactionList";
import CMPaymentViewModel from "@/models/ViewModels/CMPaymentViewModel";
import CMPayment from "@/models/APIModels/CMPayment";
import { UserViewModel } from "@/models/ViewModels/UserModel";
import IResponseModel from "@/api/ResponseModel";
import UserUsecase from "./UserUsecase";
import TokenUseCase from "./TokenUseCase";
import { User } from "@/models";
import store from "@/store";

export default class CommitteeMemberUseCase {
  private readonly _committeeMemberApi: CommitteeMemberApi;
  constructor(
    committeeMemberApi: CommitteeMemberApi = new CommitteeMemberApi(),
  ) {
    this._committeeMemberApi = committeeMemberApi;
  }

  async submitCommitteeMember(committeeMember: AdministrationPayer_Request) {
    try {
      if (committeeMember.concurrencyRV)
        return await this._committeeMemberApi.update(
          committeeMember,
          committeeMember.userReference,
        );
      else {
        const result = await this._committeeMemberApi.insert(committeeMember);
        if (result.content) {
          const tokenManager = new TokenUseCase();
          await tokenManager.setAccessToken(result.content.accessToken);
          store.state.user = result.content.user;
        }
        return {
          ...result,
          content: result.content?.user ?? undefined,
        } as IResponseModel<User>;
      }
    } catch (error) {
      throw error;
    }
  }

  async selectUser(
    userReference: string,
  ): Promise<IResponseModel<UserViewModel>> {
    try {
      const userUserCase = new UserUsecase();
      const result = await userUserCase.getUserByReference(userReference);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async setPaymentSource(
    paymentSource: PaymentSourceModel,
    schoolOfficerReference: string,
    concurrencyRV: string,
  ) {
    try {
      const result = await this._committeeMemberApi.setPaymentSource(
        paymentSource,
        schoolOfficerReference,
        concurrencyRV,
      );
      if (result.content)
        result.content = this.mapCommitteeMemberToViewModel(result.content);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async chargeAccount(userReference: string) {
    try {
      const result =
        await this._committeeMemberApi.chargeAccount(userReference);
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getTransactionsList(
    paymentStatuses: string[],
    fromDate?: string,
    toDate?: string,
  ) {
    try {
      const queryString = [];
      if (paymentStatuses.length > 0) {
        queryString.push(`paymentStatusCds=${paymentStatuses.join(",")}`);
      }
      if (fromDate)
        queryString.push(
          `fromDt=${moment(fromDate, "DD/MM/YYYY")
            .toDate()
            .toLocaleDateString()}`,
        );
      if (toDate)
        queryString.push(
          `toDt=${moment(toDate, "DD/MM/YYYY").toDate().toLocaleDateString()}`,
        );
      const result = await this._committeeMemberApi.getTransactionsList(
        queryString.join("&"),
      );

      if (result.content) {
        result.content = this.mapTransactionListToViewModel(result.content);
      }

      return result;
    } catch (error) {
      throw error;
    }
  }

  async getPaymentList(filter: {
    userReference?: string;
    fromDate?: Date;
    toDate?: Date;
  }) {
    let queryStringArray: string[] = [];
    if (filter.userReference)
      queryStringArray.push(`userReference=${filter.userReference}`);
    if (filter.fromDate) queryStringArray.push(`fromDate=${filter.fromDate}`);
    if (filter.toDate) queryStringArray.push(`toDate=${filter.toDate}`);
    const queryString = queryStringArray.join("&");
    const result = await this._committeeMemberApi.getPaymentList(queryString);
    if (result.content) {
      result.content = result.content.map((item) =>
        this.mapCMPaymentToViewModel(item),
      );
    }
    return result;
  }

  private mapCMPaymentToViewModel(cmPayment: CMPayment): CMPaymentViewModel {
    return { ...cmPayment };
  }
  private mapCommitteeMemberFromViewModel(
    committeeMember: AdministrationPayerViewModel,
  ): AdministrationPayer {
    return { ...committeeMember };
  }

  private mapCommitteeMemberToViewModel(
    committeeMember: AdministrationPayer,
  ): AdministrationPayerViewModel {
    return { ...committeeMember };
  }

  private mapTransactionListToViewModel(
    transactionList: IncomingPaymentTransactionList,
  ): IncomingPaymentTransactionList {
    return { ...transactionList };
  }
}
